import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import ApiMethods from "../../../../ApiManager/ApiMethods";
import { useAlert } from "../../../context/AlertContext";
import useLockBodyScroll from "../../../hooks/useLockBodyScroll";
import DateField from "./DateField";
import { getAssignablePackages } from "../../../data/packagesCatalogStore";
import { PACKAGE_CATALOG } from "../../../../common/utils/packageCatalog";
import "./packageFlow.css";

// Same override used by PackageAssignFlow.jsx's catalog match — this dropdown edits/creates a
// real client_package row directly (no confirm-step gating like the assign flow), so it only
// ever needs to relabel the three single-vežbač tiers that already exist as real backend
// packages; anything else an admin may have added shows its own DB name unchanged.
const isTrialLike = (pkg) => /trial|besplat/i.test(pkg?.name || "");
const displayLabelFor = (pkg) => {
  if (isTrialLike(pkg)) return pkg.name;
  const entry = PACKAGE_CATALOG.find(
    (e) => e.matchServiceQty === Number(pkg.service_qty)
  );
  if (!entry) return pkg.name;
  return `${entry.name} — ${Number(entry.price).toLocaleString("sr-RS")} RSD`;
};

const useIsDesktopTablet = () => {
  const [isDesktopTablet, setIsDesktopTablet] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsDesktopTablet(window.innerWidth >= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isDesktopTablet;
};

// `editingPackage` is null for "add", or a clients_packages row (with package_id/start_date) for "edit".
// Rebuilt on this app's own `.hf-pkg-*` sheet/modal system (packageFlow.css, shared with
// PackageAssignFlow/PackageDetailsModal) instead of a raw MUI Dialog, so it looks and behaves
// consistently with the rest of the package flow.
const ClientPackageDialog = ({ open, onClose, clientId, editingPackage, onSaved }) => {
  const { showAlert } = useAlert();
  const isDesktopTablet = useIsDesktopTablet();
  // Always mounted (returns null when !open below), so the lock is gated on `open`.
  useLockBodyScroll(open);
  const [packages, setPackages] = useState([]);
  const [packageId, setPackageId] = useState("");
  const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
  const [paymentDate, setPaymentDate] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    getAssignablePackages()
      .then((res) => setPackages(res || []))
      .catch((err) => console.log(err));

    if (editingPackage) {
      setPackageId(editingPackage.package_id);
      setStartDate(moment(editingPackage.start_date).format("YYYY-MM-DD"));
      setPaymentDate(
        editingPackage.payment_date
          ? moment(editingPackage.payment_date).format("YYYY-MM-DD")
          : ""
      );
    } else {
      setPackageId("");
      setStartDate(moment().format("YYYY-MM-DD"));
      setPaymentDate("");
    }
  }, [open, editingPackage]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const handleSave = async () => {
    if (!packageId || !startDate) return;
    setSaving(true);
    try {
      let response;
      if (editingPackage) {
        response = await ApiMethods.post(
          `client-packages/${editingPackage.id}/update`,
          {
            package_id: packageId,
            start_date: startDate,
            payment_date: paymentDate || null,
          }
        );
      } else {
        response = await ApiMethods.post("client-packages/create", {
          client_id: clientId,
          package_id: packageId,
          start_date: startDate,
          payment_date: paymentDate || undefined,
          created_by: localStorage.getItem("id"),
        });
      }
      showAlert(
        editingPackage ? "Paket je izmenjen" : "Paket je dodat",
        "success"
      );
      onSaved(response?.clientPackage);
    } catch (err) {
      console.log(err);
      showAlert("Došlo je do greške", "error");
    } finally {
      setSaving(false);
    }
  };

  const body = (
    <>
      <div className="hf-pkg-field">
        <label htmlFor="cp-package">Paket</label>
        <select
          id="cp-package"
          className="hf-pkg-input hf-pkg-select"
          value={packageId}
          onChange={(e) => setPackageId(Number(e.target.value))}
        >
          <option value="" disabled>
            Izaberite paket
          </option>
          {packages.map((pkg) => (
            <option key={pkg.id} value={pkg.id}>
              {displayLabelFor(pkg)}
            </option>
          ))}
        </select>
      </div>

      <div className="hf-pkg-field">
        <label htmlFor="cp-start-date">Datum početka</label>
        <DateField
          id="cp-start-date"
          label="Datum početka"
          value={startDate}
          onChange={setStartDate}
        />
      </div>

      <div className="hf-pkg-field">
        <label htmlFor="cp-payment-date">Datum plaćanja</label>
        <DateField
          id="cp-payment-date"
          label="Datum plaćanja"
          value={paymentDate}
          onChange={setPaymentDate}
          allowClear
        />
        <span className="hf-pkg-hint">
          {paymentDate ? "Paket je plaćen." : "Ostavite prazno ako paket nije plaćen."}
        </span>
      </div>

      <div className="hf-pkg-footer">
        <button type="button" className="hf-pkg-btn-cancel" onClick={onClose}>
          Otkaži
        </button>
        <button
          type="button"
          className="hf-pkg-btn-primary"
          disabled={!packageId || saving}
          onClick={handleSave}
        >
          {saving ? <span className="hf-btn-spinner" /> : "Sačuvaj"}
        </button>
      </div>
    </>
  );

  const headerRow = (
    <div className="hf-pkg-header">
      <h2 className="hf-pkg-title">
        {editingPackage ? "Izmeni paket" : "Dodaj paket"}
      </h2>
      <button type="button" className="hf-pkg-close" onClick={onClose} aria-label="Zatvori">
        <CloseIcon sx={{ fontSize: 16 }} />
      </button>
    </div>
  );

  if (isDesktopTablet) {
    return (
      <div className="hf-pkg-overlay">
        <div className="hf-pkg-modal">
          {headerRow}
          {body}
        </div>
      </div>
    );
  }

  return (
    <div className="hf-pkg-overlay">
      <div className="hf-pkg-sheet">
        <div className="hf-pkg-sheet-handle" />
        {headerRow}
        {body}
      </div>
    </div>
  );
};

export default ClientPackageDialog;
